<template>
	<!--
		Painel principal da nova seção:
		"Vidas por Empresa".

		Essa seção aparece abaixo dos blocos já existentes no dashboard.
		Ela mostra:
		- total geral de vidas
		- gráfico de colunas feito com HTML/CSS
		- tabela com empresa e quantidade de vidas
	-->
	<article class="dashboard__panel lives-by-company">
		<!-- Cabeçalho da seção -->
		<div class="lives-by-company__header">
			<div>
				<!-- Título da nova seção -->
				<h3 class="lives-by-company__title">
					Vidas por Empresa
				</h3>

				<!-- Texto explicativo da seção -->
				<p class="lives-by-company__subtitle">
					Divisão real de vidas cadastradas em cada empresa.
				</p>
			</div>

			<!-- Total geral de vidas somando todas as empresas retornadas pela API -->
			<strong class="lives-by-company__total">
				Total: {{ formatNumber(totalLives) }} vidas
			</strong>
		</div>

		<!--
			Se existirem empresas retornadas pelo backend,
			mostramos o gráfico e a tabela.
		-->
		<div v-if="hasCompanies">
			<!--
				Gráfico de colunas feito sem biblioteca externa.

				Cada item de livesByCompany vira uma coluna.
				A altura da coluna é calculada com base na empresa
				que possui mais vidas.
			-->
			<div class="lives-by-company__chart">
				<div class="lives-by-company__bars">
					<div
						v-for="company in livesByCompany"
						:key="company.company_id"
						class="lives-by-company__bar-item"
					>
						<!-- Valor exibido acima da coluna -->
						<span class="lives-by-company__bar-value">
							{{ formatNumber(company.total_lives) }}
						</span>

						<!--
							Coluna visual.

							A altura vem do método getBarHeight().
							Exemplo:
							empresa com maior número de vidas fica com 100%;
							as outras ficam proporcionais.
						-->
						<div class="lives-by-company__bar-wrapper">
							<div
								class="lives-by-company__bar"
								:style="{ height: getBarHeight(company.total_lives) }"
							></div>
						</div>

						<!-- Nome da empresa abaixo da coluna -->
						<span class="lives-by-company__bar-label">
							{{ company.company_name || 'Empresa sem nome' }}
						</span>
					</div>
				</div>
			</div>

			<!--
				Tabela com a lista das empresas e a quantidade real de vidas.
			-->
			<div class="table-container mt-5">
				<table class="table is-fullwidth is-hoverable lives-by-company__table">
					<thead>
						<tr>
							<th>Empresa</th>
							<th class="has-text-right">Vidas</th>
						</tr>
					</thead>

					<tbody>
						<!--
							Percorre cada empresa retornada pelo backend.

							Formato esperado:
							{
								company_id: 1,
								company_name: 'Empresa A',
								total_lives: 4401
							}
						-->
						<tr
							v-for="company in livesByCompany"
							:key="`table-${company.company_id}`"
						>
							<td>
								{{ company.company_name || 'Empresa sem nome' }}
							</td>

							<td class="has-text-right">
								<strong>
									{{ formatNumber(company.total_lives) }}
								</strong>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>

		<!--
			Caso a API não retorne nenhuma empresa,
			mostramos uma mensagem amigável.
		-->
		<div v-else class="lives-by-company__empty">
			Nenhuma vida por empresa foi encontrada.
		</div>
	</article>
</template>

<script>
export default {
	/*
	 * Nome do componente.
	 */
	name: 'LivesByCompany',

	/*
	 * Props recebidas do Dashboard.vue.
	 *
	 * O Dashboard.vue chama esse componente assim:
	 *
	 * <LivesByCompany :lives-by-company="livesByCompany" />
	 *
	 * Ou seja, ele envia para cá a lista que veio do backend
	 * no campo lives_by_company.
	 */
	props: {
		livesByCompany: {
			type: Array,
			required: false,

			/*
			 * Valor padrão vazio.
			 *
			 * Isso evita erro enquanto a API ainda está carregando
			 * ou caso o backend não retorne lives_by_company.
			 */
			default: () => []
		}
	},

	computed: {
		/*
		 * Verifica se existe pelo menos uma empresa para exibir.
		 *
		 * Se tiver dados:
		 * mostra gráfico + tabela.
		 *
		 * Se não tiver:
		 * mostra mensagem de vazio.
		 */
		hasCompanies() {
			return this.livesByCompany.length > 0
		},

		/*
		 * Soma o total de vidas de todas as empresas.
		 *
		 * Exemplo:
		 * Empresa A: 3000
		 * Empresa B: 1000
		 * Empresa C: 401
		 *
		 * Total: 4401
		 */
		totalLives() {
			return this.livesByCompany.reduce((total, company) => {
				return total + Number(company.total_lives || 0)
			}, 0)
		},

		/*
		 * Pega o maior número de vidas entre as empresas.
		 *
		 * Esse valor é usado para calcular a altura proporcional
		 * das colunas do gráfico.
		 *
		 * Exemplo:
		 * Empresa A: 4000 vidas
		 * Empresa B: 2000 vidas
		 *
		 * Empresa A fica com a coluna em 100%.
		 * Empresa B fica com a coluna em 50%.
		 */
		maxLives() {
			if (!this.hasCompanies) {
				return 0
			}

			return Math.max(
				...this.livesByCompany.map((company) => {
					return Number(company.total_lives || 0)
				})
			)
		}
	},

	methods: {
		/*
		 * Formata números no padrão brasileiro.
		 *
		 * Exemplo:
		 * 4401 vira 4.401
		 */
		formatNumber(value) {
			const number = Number(value || 0)

			return number.toLocaleString('pt-BR', {
				minimumFractionDigits: 0,
				maximumFractionDigits: 0
			})
		},

		/*
		 * Calcula a altura da coluna no gráfico.
		 *
		 * A empresa com mais vidas recebe 100% da altura.
		 * As outras empresas recebem altura proporcional.
		 *
		 * Também usamos um mínimo de 8% para empresas com poucas vidas,
		 * evitando que a coluna fique invisível.
		 */
		getBarHeight(value) {
			const total = Number(value || 0)

			if (this.maxLives <= 0 || total <= 0) {
				return '0%'
			}

			const percentage = (total / this.maxLives) * 100
			const safePercentage = Math.max(percentage, 8)

			return `${safePercentage}%`
		}
	}
}
</script>